// 纯计算工具：相似变换、凸包、掩码形态学运算。不依赖 DOM，方便单测。

export type Pt = [number, number];

/** x' = a·x − b·y + tx,  y' = b·x + a·y + ty（等比缩放 + 旋转 + 平移，不会改变脸型） */
export interface Similarity {
  a: number;
  b: number;
  tx: number;
  ty: number;
}

export const IDENTITY: Similarity = { a: 1, b: 0, tx: 0, ty: 0 };

/** 最小二乘求 src → dst 的相似变换（Umeyama，不允许镜像） */
export function estimateSimilarity(src: Pt[], dst: Pt[]): Similarity {
  const n = Math.min(src.length, dst.length);
  if (n < 2) return IDENTITY;
  let sx = 0;
  let sy = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < n; i++) {
    sx += src[i][0];
    sy += src[i][1];
    dx += dst[i][0];
    dy += dst[i][1];
  }
  sx /= n;
  sy /= n;
  dx /= n;
  dy /= n;
  let sxx = 0;
  let sxy = 0;
  let varS = 0;
  for (let i = 0; i < n; i++) {
    const px = src[i][0] - sx;
    const py = src[i][1] - sy;
    const qx = dst[i][0] - dx;
    const qy = dst[i][1] - dy;
    sxx += px * qx + py * qy;
    sxy += px * qy - py * qx;
    varS += px * px + py * py;
  }
  if (varS === 0) return IDENTITY;
  const a = sxx / varS;
  const b = sxy / varS;
  return { a, b, tx: dx - (a * sx - b * sy), ty: dy - (b * sx + a * sy) };
}

export function applySimilarity(t: Similarity, p: Pt): Pt {
  return [t.a * p[0] - t.b * p[1] + t.tx, t.b * p[0] + t.a * p[1] + t.ty];
}

export function similarityScale(t: Similarity): number {
  return Math.hypot(t.a, t.b);
}

export function similarityAngleDeg(t: Similarity): number {
  return (Math.atan2(t.b, t.a) * 180) / Math.PI;
}

/** 变换是否合理：模型只是轻微偏移/缩放时才对齐，离谱的结果说明关键点识别错了 */
export function isPlausible(t: Similarity, width: number, height: number): boolean {
  const s = similarityScale(t);
  const ang = Math.abs(similarityAngleDeg(t));
  const cx = width / 2;
  const cy = height / 2;
  const [mx, my] = applySimilarity(t, [cx, cy]);
  const shift = Math.hypot(mx - cx, my - cy) / Math.max(width, height);
  return s > 0.8 && s < 1.25 && ang < 12 && shift < 0.2;
}

export function meanError(t: Similarity, src: Pt[], dst: Pt[]): number {
  let sum = 0;
  const n = Math.min(src.length, dst.length);
  for (let i = 0; i < n; i++) {
    const [x, y] = applySimilarity(t, src[i]);
    sum += Math.hypot(x - dst[i][0], y - dst[i][1]);
  }
  return n ? sum / n : 0;
}

/** Andrew 单调链凸包，返回逆时针顶点 */
export function convexHull(points: Pt[]): Pt[] {
  const pts = [...points].sort((p, q) => p[0] - q[0] || p[1] - q[1]);
  if (pts.length <= 2) return pts;
  const cross = (o: Pt, a: Pt, b: Pt) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Pt[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Pt[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}

/** 扫描线填充多边形到 0/255 掩码（偶奇规则） */
export function fillPolygon(mask: Uint8Array, w: number, h: number, poly: Pt[], value = 255): void {
  if (poly.length < 3) return;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const p of poly) {
    minY = Math.min(minY, p[1]);
    maxY = Math.max(maxY, p[1]);
  }
  const y0 = Math.max(0, Math.floor(minY));
  const y1 = Math.min(h - 1, Math.ceil(maxY));
  const xs: number[] = [];
  for (let y = y0; y <= y1; y++) {
    const cy = y + 0.5;
    xs.length = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const [xi, yi] = poly[i];
      const [xj, yj] = poly[j];
      if (yi > cy !== yj > cy) xs.push(xi + ((cy - yi) / (yj - yi)) * (xj - xi));
    }
    xs.sort((a, b) => a - b);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const xa = Math.max(0, Math.ceil(xs[k] - 0.5));
      const xb = Math.min(w - 1, Math.floor(xs[k + 1] - 0.5));
      for (let x = xa; x <= xb; x++) mask[y * w + x] = value;
    }
  }
}

/** 可分离的盒式滤波（近似高斯羽化），原地返回新数组 */
export function boxBlur(src: Uint8Array, w: number, h: number, r: number): Uint8Array {
  if (r < 1) return src.slice();
  const tmp = new Float32Array(w * h);
  const out = new Uint8Array(w * h);
  const size = 2 * r + 1;
  for (let y = 0; y < h; y++) {
    let acc = 0;
    const row = y * w;
    for (let x = -r; x <= r; x++) acc += src[row + clamp(x, 0, w - 1)];
    for (let x = 0; x < w; x++) {
      tmp[row + x] = acc / size;
      acc += src[row + clamp(x + r + 1, 0, w - 1)] - src[row + clamp(x - r, 0, w - 1)];
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let y = -r; y <= r; y++) acc += tmp[clamp(y, 0, h - 1) * w + x];
    for (let y = 0; y < h; y++) {
      out[y * w + x] = Math.round(acc / size);
      acc += tmp[clamp(y + r + 1, 0, h - 1) * w + x] - tmp[clamp(y - r, 0, h - 1) * w + x];
    }
  }
  return out;
}

/** 三次盒式滤波 ≈ 高斯 */
export function gaussianish(src: Uint8Array, w: number, h: number, r: number): Uint8Array {
  const rr = Math.max(1, Math.round(r / 1.7));
  return boxBlur(boxBlur(boxBlur(src, w, h, rr), w, h, rr), w, h, rr);
}

/** 二值膨胀（阈值 128），用方形结构元，分两趟 */
export function dilate(src: Uint8Array, w: number, h: number, r: number): Uint8Array {
  return morph(src, w, h, r, true);
}

export function erode(src: Uint8Array, w: number, h: number, r: number): Uint8Array {
  return morph(src, w, h, r, false);
}

function morph(src: Uint8Array, w: number, h: number, r: number, grow: boolean): Uint8Array {
  if (r < 1) return src.slice();
  const pass = (input: Uint8Array, horizontal: boolean) => {
    const out = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let v = grow ? 0 : 255;
        for (let k = -r; k <= r; k++) {
          const xx = horizontal ? clamp(x + k, 0, w - 1) : x;
          const yy = horizontal ? y : clamp(y + k, 0, h - 1);
          const s = input[yy * w + xx] >= 128 ? 255 : 0;
          if (grow ? s > v : s < v) v = s;
          if (grow ? v === 255 : v === 0) break;
        }
        out[y * w + x] = v;
      }
    }
    return out;
  };
  return pass(pass(src, true), false);
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** 按宽高把整张图裁成 3:4 人像框，脸在中上部 */
export function portraitCrop(
  imgW: number,
  imgH: number,
  face: { cx: number; top: number; width: number; height: number } | null,
  aspect = 3 / 4,
): { x: number; y: number; w: number; h: number } {
  let w: number;
  let h: number;
  if (face) {
    // 脸宽约占画面 40%，额头上方留足空间给发型
    w = Math.min(imgW, face.width / 0.4);
    h = w / aspect;
    if (h > imgH) {
      h = imgH;
      w = h * aspect;
    }
    let x = face.cx - w / 2;
    let y = face.top - h * 0.24;
    x = clamp(x, 0, imgW - w);
    y = clamp(y, 0, imgH - h);
    return { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) };
  }
  if (imgW / imgH > aspect) {
    h = imgH;
    w = h * aspect;
  } else {
    w = imgW;
    h = w / aspect;
  }
  return { x: Math.round((imgW - w) / 2), y: Math.round((imgH - h) / 2), w: Math.round(w), h: Math.round(h) };
}
